import { cn } from '@/lib/utils';

const fmtHours = (minutes: number): string => {
  if (!minutes || minutes <= 0) return '0h';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};

export interface UtilizationBarProps {
  totalMinutes: number;
  billableMinutes: number;
  className?: string;
}

/**
 * Reports redesign — gradient utilization meter for the by-user
 * table. Fill width = `billable / total`; renders the percentage and
 * a "Xh / Yh" sub-line. Caps at 100% for safety even though the
 * inputs should never exceed that.
 *
 * The blue-to-orange gradient matches the prototype's
 * `linear-gradient(90deg, oklch(0.7 0.13 245), oklch(0.74 0.16 50))`
 * so each user row reads as a contiguous brand-aligned indicator.
 */
export const UtilizationBar = ({
  totalMinutes,
  billableMinutes,
  className,
}: UtilizationBarProps) => {
  const pct =
    totalMinutes > 0
      ? Math.min(100, Math.round((billableMinutes / totalMinutes) * 100))
      : 0;

  return (
    <div className={cn('flex min-w-0 flex-col gap-1', className)}>
      <div
        className={cn(
          'relative h-[6px] overflow-hidden rounded-full',
          'bg-[var(--surface-quiet,var(--secondary))]',
        )}
      >
        <div
          className={cn(
            'absolute inset-y-0 left-0 rounded-full',
            'bg-gradient-to-r from-[oklch(0.7_0.13_245)] to-[oklch(0.74_0.16_50)]',
            'shadow-[0_0_6px_oklch(0.7_0.14_245/0.4)]',
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div
        className={cn(
          'flex justify-between font-mono text-[10.5px] tabular-nums',
          'text-foreground-faint',
        )}
      >
        <span>
          <strong className="text-foreground-muted font-medium">{pct}%</strong>{' '}
          billable
        </span>
        <span>
          {fmtHours(billableMinutes)} / {fmtHours(totalMinutes)}
        </span>
      </div>
    </div>
  );
};
